/**
 * PDF Export utility for conversations
 * Uses HTML to PDF conversion via print API
 */

/**
 * Export conversation to PDF
 * @param {Array} messages - Array of message objects
 * @param {string} conversationTitle - Title of the conversation
 */
export const exportConversationToPDF = (messages, conversationTitle = 'Conversation') => {
  try {
    // Filter out non-displayable messages
    const displayableMessages = messages.filter(msg => 
      !msg.isIntermediate && 
      !msg.isFunctionCall &&
      msg.text && 
      msg.text.trim() !== ''
    );

    // Create a hidden iframe for PDF generation
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
    
    // Build HTML for PDF
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${escapeHtml(conversationTitle)}</title>
        <style>
          @page {
            margin: 2cm;
          }
          
          * {
            box-sizing: border-box;
          }
          
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica', 'Arial', sans-serif;
            line-height: 1.6;
            color: #1F2937;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            background: white;
          }
          
          h1 {
            font-size: 28px;
            color: #111827;
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 3px solid #4F46E5;
          }
          
          .export-info {
            color: #6B7280;
            font-size: 12px;
            margin-bottom: 30px;
            padding-bottom: 15px;
            border-bottom: 1px solid #E5E7EB;
          }
          
          .message {
            margin-bottom: 25px;
            padding: 15px;
            border-radius: 8px;
            page-break-inside: avoid;
          }
          
          .message-user {
            background: #EEF2FF;
            border-left: 4px solid #4F46E5;
          }
          
          .message-assistant {
            background: #F3F4F6;
            border-left: 4px solid #10B981;
          }
          
          .message-error {
            background: #FEE2E2;
            border-left: 4px solid #EF4444;
          }
          
          .message-info {
            background: #DBEAFE;
            border-left: 4px solid #3B82F6;
          }
          
          .message-header {
            font-weight: 600;
            font-size: 14px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          .message-user .message-header {
            color: #4338CA;
          }
          
          .message-assistant .message-header {
            color: #059669;
          }
          
          .message-error .message-header {
            color: #DC2626;
          }
          
          .message-info .message-header {
            color: #2563EB;
          }
          
          .message-timestamp {
            font-size: 11px;
            color: #9CA3AF;
            font-weight: normal;
          }
          
          .message-content {
            font-size: 14px;
            white-space: pre-wrap;
            word-wrap: break-word;
          }
          
          .message-content code {
            background: rgba(0, 0, 0, 0.05);
            padding: 2px 6px;
            border-radius: 3px;
            font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
            font-size: 13px;
          }
          
          .message-content pre {
            background: #1F2937;
            color: #F3F4F6;
            padding: 12px;
            border-radius: 6px;
            overflow-x: auto;
            font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
            font-size: 12px;
            line-height: 1.5;
          }
          
          .message-content pre code {
            background: transparent;
            padding: 0;
            color: #F3F4F6;
          }
          
          .message-content a {
            color: #4F46E5;
            text-decoration: none;
          }
          
          .message-content a:hover {
            text-decoration: underline;
          }
          
          .message-content ul,
          .message-content ol {
            margin: 10px 0;
            padding-left: 25px;
          }
          
          .message-content li {
            margin: 5px 0;
          }
          
          .message-content blockquote {
            border-left: 3px solid #D1D5DB;
            margin: 10px 0;
            padding-left: 15px;
            color: #6B7280;
            font-style: italic;
          }
          
          .message-content h1,
          .message-content h2,
          .message-content h3,
          .message-content h4,
          .message-content h5,
          .message-content h6 {
            margin-top: 20px;
            margin-bottom: 10px;
            font-weight: 600;
          }
          
          .message-content hr {
            border: none;
            border-top: 1px solid #E5E7EB;
            margin: 20px 0;
          }
          
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #E5E7EB;
            text-align: center;
            color: #9CA3AF;
            font-size: 11px;
          }
          
          @media print {
            body {
              padding: 0;
            }
            
            .message {
              page-break-inside: avoid;
            }
          }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(conversationTitle)}</h1>
        <div class="export-info">
          Exported on ${new Date().toLocaleString()}<br>
          Total messages: ${displayableMessages.length}
        </div>
        
        ${displayableMessages.map(msg => {
          const roleClass = msg.role === 'user' ? 'message-user' : 
                           msg.role === 'assistant' ? 'message-assistant' :
                           msg.role === 'error' ? 'message-error' : 'message-info';
          
          const roleLabel = msg.role === 'user' ? '👤 You' :
                           msg.role === 'assistant' ? '🤖 Assistant' :
                           msg.role === 'error' ? '⚠️ Error' : 'ℹ️ Info';
          
          const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
          
          return `
            <div class="message ${roleClass}">
              <div class="message-header">
                ${roleLabel}
                ${timestamp ? `<span class="message-timestamp">${timestamp}</span>` : ''}
              </div>
              <div class="message-content">${formatMessageContent(msg.text)}</div>
            </div>
          `;
        }).join('')}
        
        <div class="footer">
          Generated by Mia-Platform AI Coder
        </div>
      </body>
      </html>
    `;

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    // Wait for content to load, then print
    iframe.contentWindow.addEventListener('load', () => {
      setTimeout(() => {
        iframe.contentWindow.print();
        // Clean up after a delay
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      }, 250);
    });

  } catch (error) {
    console.error('Error exporting to PDF:', error);
    alert('Failed to export conversation to PDF. Please try again.');
  }
};

/**
 * Format message content with basic markdown-like rendering
 */
function formatMessageContent(text) {
  if (!text) return '';
  
  let formatted = escapeHtml(text);
  
  // Convert code blocks
  formatted = formatted.replace(/```(\w+)?\n([\s\S]*?)```/g, (match, lang, code) => {
    return `<pre><code>${code}</code></pre>`;
  });
  
  // Convert inline code
  formatted = formatted.replace(/`([^`]+)`/g, '<code>$1</code>');
  
  // Convert bold
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/__([^_]+)__/g, '<strong>$1</strong>');
  
  // Convert italic
  formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  formatted = formatted.replace(/_([^_]+)_/g, '<em>$1</em>');
  
  // Convert links
  formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank">$1</a>');
  
  // Convert line breaks
  formatted = formatted.replace(/\n/g, '<br>');
  
  return formatted;
}

/**
 * Escape HTML characters
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export default {
  exportConversationToPDF
};
